import axios from 'axios';
import store from '../../store';

// Create axios instance for authenticated API calls
export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const state = store.getState().common;

    // Add JWT token if available and not in demo mode
    if (state.jwtToken && !state.demoMode) {
      config.headers.Authorization = state.jwtToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const state = store.getState().common;

      // Only redirect to login if not in demo mode
      if (!state.demoMode) {
        console.error('Authentication token expired or invalid');

        // Store current location for redirect after login
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/' && currentPath !== '/callback') {
          sessionStorage.setItem('returnUrl', currentPath);
        }

        // Redirect to home which will trigger OIDC login
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// Create separate axios instance for demo API calls (no auth)
export const demoApiClient = axios.create({
  baseURL: process.env.REACT_APP_DEMO_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// No auth interceptors for demo client
demoApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Demo API error:', error);
    return Promise.reject(error);
  }
);

// Helper function to get the appropriate client
export const getApiClient = () => {
  const state = store.getState().common;
  return state.demoMode ? demoApiClient : apiClient;
};

// Legacy support - export default client for backward compatibility
// This will be replaced in actions.js
const axios_default = {
  get: (url, config = {}) => {
    const client = getApiClient();
    // Extract base URL from full URL if needed
    const cleanUrl = url.replace(process.env.REACT_APP_BASE_URL, '')
                       .replace(process.env.REACT_APP_DEMO_BASE_URL, '');
    return client.get(cleanUrl, config);
  },
  post: (url, data, config = {}) => {
    const client = getApiClient();
    const cleanUrl = url.replace(process.env.REACT_APP_BASE_URL, '')
                       .replace(process.env.REACT_APP_DEMO_BASE_URL, '');
    return client.post(cleanUrl, data, config);
  },
  put: (url, data, config = {}) => {
    const client = getApiClient();
    const cleanUrl = url.replace(process.env.REACT_APP_BASE_URL, '')
                       .replace(process.env.REACT_APP_DEMO_BASE_URL, '');
    return client.put(cleanUrl, data, config);
  },
  delete: (url, config = {}) => {
    const client = getApiClient();
    const cleanUrl = url.replace(process.env.REACT_APP_BASE_URL, '')
                       .replace(process.env.REACT_APP_DEMO_BASE_URL, '');
    return client.delete(cleanUrl, config);
  }
};

export default axios_default;